<script setup lang="ts">
/**
 * ProgressProtectionMockup — 进度条防护 mockup
 * v8 真实能力（hide.js 注入 bar.css / time.css + progress-bar-controller）：
 *   - barProtection：隐藏进度条与总时长
 *   - timeProtection：隐藏结束时间
 * 观看时无法通过进度条与时长推断比赛阶段与结果，避免被"进度"提前剧透。
 */
import { ref, computed } from 'vue'

const protectedOn = ref(true)
const seconds = ref(137)
const totalSeconds = 45 * 60 + 15 // 45:15

const sliderPercent = computed(() => (seconds.value / totalSeconds) * 100)

function formatTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

function toggleProtection() {
  protectedOn.value = !protectedOn.value
}
</script>

<template>
  <div class="pp-mock">
    <div class="pp-mock__header">
      <span class="pp-mock__label">进度条防护</span>
      <button type="button" class="pp-mock__toggle yfb-press" @click="toggleProtection">
        {{ protectedOn ? '防护开' : '防护关' }}
      </button>
    </div>
    <div class="pp-mock__player">
      <div class="pp-mock__video">
        <div class="pp-mock__frame">
          <div class="pp-mock__time">
            <!-- 防护开：只显示当前时间，无总时长 -->
            <template v-if="protectedOn">
              <span class="pp-mock__time-current">{{ formatTime(seconds) }}</span>
              <span class="pp-mock__time-total-mask">[总时长]</span>
            </template>
            <!-- 防护关：当前 + 总长 + 结束时间 -->
            <template v-else>
              {{ formatTime(seconds) }} / {{ formatTime(totalSeconds) }}
              <span class="pp-mock__time-end">· 结束 {{ formatTime(totalSeconds) }}</span>
            </template>
          </div>

          <!-- 进度条 -->
          <div v-if="!protectedOn" class="pp-mock__bar">
            <div class="pp-mock__bar-fill" :style="{ width: sliderPercent + '%' }"></div>
          </div>
          <!-- 防护开：进度条区域被隐藏占位 -->
          <div v-else class="pp-mock__bar-mask">
            <span class="pp-mock__bar-mask-label">[进度条已隐藏]</span>
          </div>
        </div>

        <div class="pp-mock__hint" :class="{ 'pp-mock__hint--on': protectedOn }">
          {{
            protectedOn
              ? '看不到进度与结束时间 · 无法推断比赛阶段'
              : '进度条与结束时间可见 · 易被剧透'
          }}
        </div>
      </div>
    </div>
    <div class="pp-mock__footer">时长不暴露 · 结果无从推断</div>
  </div>
</template>

<style scoped>
.pp-mock {
  border: var(--yfb-border-2) solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  box-shadow: var(--yfb-shadow-2);
  border-radius: var(--yfb-radius-lg);
  overflow: hidden;
}

.pp-mock__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  border-bottom: 1px solid var(--yfb-rule-soft);
}

.pp-mock__label {
  font-family: var(--yfb-font-heading);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-accent);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
}

.pp-mock__toggle {
  padding: 4px 12px;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border: none;
  border-radius: var(--yfb-radius-sm);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
}

.pp-mock__toggle:hover {
  box-shadow: var(--yfb-shadow-hit);
}

.pp-mock__player {
  padding: var(--yfb-sp-4);
}

.pp-mock__video {
  background-color: var(--yfb-bg);
  padding: var(--yfb-sp-4);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
  border-radius: var(--yfb-radius-md);
  box-shadow: inset 0 0 0 1px var(--yfb-rule-soft);
}

.pp-mock__frame {
  position: relative;
  aspect-ratio: 16 / 9;
  background-color: var(--yfb-panel-bg);
  border: var(--yfb-border-2) solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2);
}

.pp-mock__frame::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px),
    repeating-linear-gradient(90deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px);
  pointer-events: none;
}

/* 时间区 */
.pp-mock__time {
  position: relative;
  z-index: 1;
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-accent);
  letter-spacing: var(--yfb-ls-mono);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
}

.pp-mock__time-current {
  background: rgba(11, 11, 16, 0.7);
  padding: 2px 6px;
  border-radius: var(--yfb-radius-sm);
}

.pp-mock__time-total-mask {
  font-size: 9px;
  color: var(--yfb-text-secondary);
  background: var(--yfb-surface);
  border: 1px dashed var(--yfb-rule-soft);
  padding: 2px 6px;
  border-radius: var(--yfb-radius-sm);
}

.pp-mock__time-end {
  font-size: 9px;
  color: var(--yfb-signal-danger);
}

/* 进度条（防护关） */
.pp-mock__bar {
  position: relative;
  z-index: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  overflow: hidden;
}

.pp-mock__bar-fill {
  height: 100%;
  background: var(--yfb-accent);
}

/* 进度条（防护开：隐藏占位） */
.pp-mock__bar-mask {
  position: relative;
  z-index: 1;
  height: 10px;
  display: flex;
  align-items: center;
}

.pp-mock__bar-mask-label {
  font-family: var(--yfb-mono);
  font-size: 8px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  background: var(--yfb-surface);
  border: 1px dashed var(--yfb-rule-soft);
  padding: 2px 6px;
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-mono);
}

.pp-mock__hint {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.pp-mock__hint--on {
  color: var(--yfb-accent);
}

.pp-mock__footer {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border-top: 1px solid var(--yfb-rule);
  justify-content: center;
}
</style>
